import React, { useState, useEffect, createContext, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { imageUrl, currencySymbols } from '../../../../Components/helper';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import * as poTemplateAction from '../../../Library/PurchaseOrderTemplate/store/POTemplate.action';
import * as productPurchaseOrderAction from '../Store/productPurchaseOrder.action';
import noImage from '../../../../assets/images/no-image.png';

const POCreationContext = createContext(null);

function getBrandIdForFilter(brand) {
  if (brand == null) return null;
  if (typeof brand === 'object') {
    const id = brand._id ?? brand.value;
    return id != null ? String(id) : null;
  }
  return String(brand);
}

function toLocalDateOnly(value) {
  if (value === null || value === undefined) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const initialState = {
  sections: [],
  sectionValues: {},
  validationErrors: {},
  isLoading: false,
  templateSelected: false,
  isPreviewMode: false,
  isNavigatingAfterCreate: false,
};

export function POCreationProvider({ children }) {
  const reduxDispatch = useDispatch();
  const reduxState = useSelector((state) => state);
  const { t } = useTranslation('purchase-order');
  const {
    UserDetails: { userInfo },
    productPurchaseOrderReducer: { poProductDetails, poTemplateDetails, purchaseOrderDetails },
  } = reduxState;
  const templateDetails = poTemplateDetails;
  const companyURL = window.location.hostname?.split('.')?.[0];
  const userInfoLogo = userInfo?.companyLogo;
  const PACK_TYPE_MULTI_SIZE = 'multi-size-packed';
  const DATE_FIELDS_FOR_MULTI_SIZE = ['approvedByDate', 'exFactoryDate', 'shipmentDate'];

  useEffect(() => {
    if (localStorage.getItem('redirectToPO')) {
      localStorage.removeItem('redirectToPO');
    }
    if (localStorage.getItem('redirectionToPO')) {
      localStorage.removeItem('redirectionToPO');
    }
  }, []);
  const companyLogo = useMemo(() => {
    if (!userInfoLogo || userInfoLogo?.length === 0) return noImage;

    if (userInfoLogo.includes('super-admin')) {
      return `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${userInfoLogo}`;
    }
    if (userInfoLogo.includes('s3.eu-west-2')) {
      return userInfoLogo;
    }
    if (userInfoLogo.startsWith('http') || userInfoLogo.startsWith('https')) {
      const href = new URL(userInfoLogo);
      return `${imageUrl}${href.pathname}`;
    }
    return `${imageUrl}/${userInfoLogo}`;
  }, [userInfoLogo]);

  const [creationState, setCreationState] = useState({
    ...initialState,
    companyLogo,
    templateName: '',
    purchaseOrderNumber: '',
    purchaseOrderId: null,
    selectedSupplier: null,
    selectedFactory: null,
    selectedTemplate: null,
    selectedBrand: null,
    poImage: null,
    POCreatedDate: new Date(),
    POSubmittedDate: null,
    PORevisedDate: null,
    status: null,
    isRefreshingAfterSave: false,
    shippingDetails: {
      shipmentMethod: 'sea',
      incoterms: '',
      shippingCosts: '',
      portOfLanding: '',
      portOfLoading: '',
    },
    grandTotal: {
      grandTotalUnits: '',
      grandTotalPriceAfterDiscount: '',
      shippingCosts: '',
    },
    orderDetails: [],
    companyDetails: {},
    userDetails: {},
    supplierDetails: {},
  });

  const isFieldsDisabledByStatus = useCallback(() => {
    const editableStatuses = ['draft', 'revised'];
    return creationState.status && !editableStatuses.includes(creationState.status.toLowerCase());
  }, [creationState.status]);

  const currencySymbol = useMemo(() => {
    const currency = userInfo?.companyCurrency;
    return currencySymbols[currency]?.symbol || '$';
  }, [userInfo?.companyCurrency]);

  const processControl = useCallback((control) => {
    const processed = {
      ...control,
      enabled: control.type !== 'ImageUpload',
      editable: control.id !== 'templateName',
      value: control.value || control.defaultValue || '',
    };
    if (control.characterLimit !== undefined && control.characterLimit !== null && Number(control.characterLimit) > 0) {
      processed.characterLimit = Number(control.characterLimit);
    }
    return processed;
  }, []);

  const processSections = useCallback(
    (sections) => {
      if (!sections) return [];
      return sections.map((section) => ({
        ...section,
        columns: section.columns?.map((column) => column?.map(processControl) || []) || [],
      }));
    },
    [processControl],
  );

  const normalizeShippingDetails = useCallback(
    (shippingDetails) => {
      if (!shippingDetails) return creationState.shippingDetails;

      return {
        shipmentMethod: shippingDetails.shippingMethod || 'sea',
        incoterms: shippingDetails.incoterms || '',
        shippingCosts: shippingDetails.shippingAndHandlingCosts || '',
        portOfLanding: shippingDetails.portOfLanding || '',
        portOfLoading: shippingDetails.portOfLading || '',
      };
    },
    [creationState.shippingDetails],
  );

  const applyProductLevelOverrides = useCallback(
    (templateData) => {
      if (!templateData) return null;

      const orderDetailsWithSequence = (templateData.orderDetails || []).map((order, index) => ({
        ...order,
        templateSequence: order.templateSequence !== undefined ? order.templateSequence : index,
      }));

      const orderDetailsNormalized = orderDetailsWithSequence.map((row) => {
        if ((row.packType === PACK_TYPE_MULTI_SIZE || row.packType === 'multi-size-packed') && !row.isSubRow) {
          const qty = (Number(row.ratio) || 0) * (Number(row.packs) || 0);
          return { ...row, quantity: qty };
        }
        return row;
      });

      return {
        sections: processSections(templateData.sections),
        companyDetails: templateData.companyDetails || {},
        userDetails: templateData.userDetails || {},
        supplierDetails: templateData.supplierDetails || {},
        shippingDetails: normalizeShippingDetails(templateData.shippingDetails),
        grandTotal: templateData.grandTotal || creationState.grandTotal,
        orderDetails: orderDetailsNormalized,
        templateName: templateData.templateName || '',
        companyLogo: templateData.companyLogo || creationState.companyLogo,
        originalPOImage: templateData.originalPOImage || creationState.originalPOImage,
      };
    },
    [processSections, normalizeShippingDetails, creationState.grandTotal, creationState.companyLogo, creationState.originalPOImage],
  );

  const populateFromPurchaseOrderDetails = useCallback(
    (orderDetails) => {
      if (!orderDetails) return null;

      const processedSections = processSections(orderDetails.sections);

      const sectionValues = {};
      if (processedSections && Array.isArray(processedSections)) {
        processedSections.forEach((section) => {
          if (section.columns && Array.isArray(section.columns)) {
            section.columns.forEach((column, colIdx) => {
              if (Array.isArray(column)) {
                column.forEach((control, idx) => {
                  const uniqueControlId = `${section.id}_${colIdx}_${control.id}_${idx}`;
                  const controlValue = control.value ?? control.defaultValue ?? '';
                  if (controlValue !== '' && controlValue !== null && controlValue !== undefined) {
                    sectionValues[uniqueControlId] = controlValue;
                  }
                });
              }
            });
          }
        });
      }

      return {
        purchaseOrderId: orderDetails._id,
        purchaseOrderNumber: orderDetails.purchaseOrderNumber || '',
        purchaseOrderDate: orderDetails.purchaseOrderDate || null,
        templateName: orderDetails.templateName || '',
        status: orderDetails.status || null,
        selectedSupplier: orderDetails.supplierId
          ? {
              _id: orderDetails.supplierId._id || orderDetails.supplierId,
              label:
                orderDetails.supplierId?.supplierName ||
                orderDetails.supplierFactoryDetails?.supplierName ||
                `${orderDetails.supplierId?.firstName || ''} ${orderDetails.supplierId?.lastName || ''}`.trim() ||
                '',
              value: orderDetails.supplierId._id || orderDetails.supplierId,
            }
          : null,
        selectedFactory: orderDetails.factoryId
          ? {
              _id: orderDetails.factoryId._id || orderDetails.factoryId,
              label: orderDetails.factoryId?.factoryName || orderDetails.supplierFactoryDetails?.factoryName || '',
              value: orderDetails.factoryId._id || orderDetails.factoryId,
            }
          : null,
        selectedTemplate: orderDetails.poTemplateId
          ? {
              value: orderDetails.poTemplateId._id || orderDetails.poTemplateId,
              label: orderDetails.poTemplateId?.templateName || orderDetails.templateName || '',
            }
          : null,
        selectedBrand: (() => {
          const poBrand = orderDetails.brandId;
          if (poBrand) {
            return {
              _id: poBrand._id || poBrand,
              name: poBrand?.name,
              label: poBrand?.name,
              value: poBrand._id || poBrand,
            };
          }
          const lineItems = orderDetails.orderDetails || [];
          const productBrand = lineItems.find(
            (line) => line?.productNumber?.brandId
          )?.productNumber?.brandId;
          if (productBrand) {
            return {
              _id: productBrand._id || productBrand,
              name: productBrand?.name,
              label: productBrand?.name,
              value: productBrand._id || productBrand,
            };
          }
          return null;
        })(),
        companyLogo: orderDetails.companyLogo || creationState.companyLogo,
        originalPOImage: orderDetails.originalPOImage || '',

        sections: processedSections,
        sectionValues,
        companyDetails: orderDetails.companyDetails || {},
        userDetails: orderDetails.userDetails || {},
        supplierDetails: orderDetails.supplierFactoryDetails || {},

        shippingDetails: {
          shipmentMethod: orderDetails.shippingDetails?.shippingMethod || '',
          incoterms: orderDetails.shippingDetails?.incoterms || '',
          shippingCosts: orderDetails.shippingDetails?.shippingAndHandlingCosts || '',
          portOfLanding: orderDetails.shippingDetails?.portOfLanding || '',
          portOfLoading: orderDetails.shippingDetails?.portOfLading || '',
        },

        grandTotal: {
          grandTotalUnits: orderDetails.grandTotal?.grandTotalUnits || '',
          grandTotalPriceAfterDiscount: orderDetails.grandTotal?.grandTotalPriceAfterDiscount || '',
          shippingCosts: orderDetails.shippingDetails?.shippingAndHandlingCosts || '',
        },

        orderDetails: (() => {
          const mapped = (orderDetails.orderDetails || []).map((order, index) => ({
            id: order._id || order.id || `order-${Date.now()}-${Math.random()}`,
            parentRowKey: order.parentRowKey || null,
            parentRowId: order.parentRowId || null,
            isSubRow: order.isSubRow || false,
            templateSequence: order.templateSequence !== undefined ? order.templateSequence : index,
            isNewlyAdded: false,

            packType: order.packType || '',
            productNo: order.productNumber ? (typeof order.productNumber === 'object' ? order.productNumber._id : order.productNumber) : '',
            colourWay: order.colourway ? (typeof order.colourway === 'object' ? order.colourway._id : order.colourway) : '',
            skuNumber: order.skuNumber || '',
            size: order.size ? (typeof order.size === 'object' ? order.size._id : order.size) : '',
            length: order.length ? (typeof order.length === 'object' ? order.length._id : order.length) : '',
            ratio: order.ratio || 0,
            packs: order.packs || 0,
            quantity: order.quantity || 0,
            location: order.location || '',
            unitPrice: order.unitPrice || 0,
            discount: order.discountPercent || 0,
            discountPercent: order.discountPercent || 0,
            priceAfterDiscount: order.priceAfterDiscount || 0,
            total: order.total || 0,
            approvedByDate: order.approvedByDate || null,
            exFactoryDate: order.exFactoryDate || null,
            shipmentDate: order.shipmentDate || null,
            revisedApprovedByDate: order.revisedApprovedByDate || null,
            revisedExFactoryDate: order.revisedExFactoryDate || null,
            revisedShipmentDate: order.revisedShipmentDate || null,
            colourwayName: order.colourwayName || order.colourWayName || (order.colourway && typeof order.colourway === 'object' ? order.colourway.name : ''),
            sizeName: order.sizeName || (order.size && typeof order.size === 'object' ? order.size.name : ''),
            lengthName: order.lengthName || (order.length && typeof order.length === 'object' ? order.length.name : ''),
          }));
          return mapped.map((row) => {
            if ((row.packType === PACK_TYPE_MULTI_SIZE || row.packType === 'multi-size-packed') && !row.isSubRow) {
              const subRows = mapped.filter((r) => r.isSubRow && (r.parentRowId === row.id || r.parentRowId === row.parentRowKey));
              const sumSubRowsQuantity = subRows.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
              return { ...row, quantity: sumSubRowsQuantity };
            }
            return row;
          });
        })(),
        POCreatedDate: orderDetails.createdAt || new Date(),
        POSubmittedDate: orderDetails.submittedDate || null,
        PORevisedDate: orderDetails.revisedDate || null,

        templateSelected: true,
      };
    },
    [creationState.companyLogo, processSections],
  );

  useEffect(() => {
    if (templateDetails && !creationState.templateSelected && !creationState.isPreviewMode) {
      const processedData = applyProductLevelOverrides(templateDetails);
      if (processedData) {
        setCreationState((prev) => ({
          ...prev,
          ...processedData,
          templateSelected: true,
          isLoading: false,
        }));
      }
    }
  }, [templateDetails]);

  useEffect(() => {
    if (purchaseOrderDetails?._id && creationState.isNavigatingAfterCreate) {
      setCreationState((prev) => ({
        ...prev,
        isNavigatingAfterCreate: false,
      }));
    }
  }, [purchaseOrderDetails?._id, creationState.isNavigatingAfterCreate]);

  useEffect(() => {
    const isCreateMode = !purchaseOrderDetails?._id && !creationState.purchaseOrderId;
    if (isCreateMode && poProductDetails?.brandId) {
      const brand = poProductDetails.brandId;
      setCreationState((prev) => ({
        ...prev,
        selectedBrand: {
          _id: brand._id || brand,
          name: brand?.name,
          label: brand?.name,
          value: brand._id || brand,
        },
      }));
    }
  }, [poProductDetails?.brandId, poProductDetails?._id, purchaseOrderDetails?._id, creationState.purchaseOrderId]);

  useEffect(() => {
    if (purchaseOrderDetails && !creationState.isPreviewMode && !creationState.isNavigatingAfterCreate) {
      const shouldPopulate = !creationState.templateSelected || (purchaseOrderDetails._id && purchaseOrderDetails._id !== creationState.purchaseOrderId);

      if (shouldPopulate) {
        const populatedData = populateFromPurchaseOrderDetails(purchaseOrderDetails);
        if (populatedData) {
          setCreationState((prev) => ({
            ...prev,
            ...populatedData,
            isLoading: false,
            isRefreshingAfterSave: prev.isRefreshingAfterSave,
          }));

          if (purchaseOrderDetails.poTemplateId && !templateDetails) {
            const templateId = purchaseOrderDetails.poTemplateId._id || purchaseOrderDetails.poTemplateId;
            reduxDispatch(productPurchaseOrderAction.fetchPOTemplateDetailsForProduct(templateId));
          }

          if (populatedData.selectedSupplier?._id) {
            reduxDispatch(
              productPurchaseOrderAction.fetchFilteredDevelopmentOrders(
                populatedData.selectedSupplier._id,
                populatedData.selectedFactory?._id || null,
                getBrandIdForFilter(populatedData.selectedBrand),
              ),
            );
          }
        }
      }
    }
  }, [purchaseOrderDetails]);

  const handleSectionValueChange = useCallback(
    (controlId, value) => {
      setCreationState((prev) => ({
        ...prev,
        sectionValues: {
          ...prev.sectionValues,
          [controlId]: value,
        },
      }));

      if (creationState.validationErrors[controlId]) {
        setCreationState((prev) => ({
          ...prev,
          validationErrors: {
            ...prev.validationErrors,
            [controlId]: '',
          },
        }));
      }
    },
    [creationState.validationErrors],
  );

  const handlePOFieldChange = useCallback((field, value) => {
    setCreationState((prev) => ({
      ...prev,
      [field]: value,
      validationErrors: prev.validationErrors[field]
        ? {
            ...prev.validationErrors,
            [field]: '',
          }
        : prev.validationErrors,
    }));
  }, []);

  const setValidationError = useCallback((field, error, index = null) => {
    setCreationState((prev) => ({
      ...prev,
      validationErrors: {
        ...prev.validationErrors,
        [field]:
          index !== null
            ? {
                ...prev.validationErrors[field],
                [index]: error,
              }
            : error,
      },
    }));
  }, []);

  const clearValidationError = useCallback((field, index = null) => {
    setCreationState((prev) => ({
      ...prev,
      validationErrors: {
        ...prev.validationErrors,
        [field]:
          index !== null
            ? {
                ...prev.validationErrors[field],
                [index]: '',
              }
            : '',
      },
    }));
  }, []);

  const clearAllValidationErrors = useCallback(() => {
    setCreationState((prev) => ({
      ...prev,
      validationErrors: {},
    }));
  }, []);

  const resetCreationContext = useCallback(() => {
    setCreationState((prev) => ({
      ...initialState,
      companyLogo: prev.companyLogo,
    }));
    reduxDispatch(productPurchaseOrderAction.clearPOTemplateDetailsForProduct());
  }, [reduxDispatch]);

  const clearAllPOData = useCallback(() => {
    setCreationState(() => ({
      ...initialState,
    }));

    reduxDispatch(productPurchaseOrderAction.clearAllPOData());
  }, [reduxDispatch]);

  const populateFromLogHistoryData = useCallback(
    (logData) => {
      if (!logData?.poSnapshot) return null;

      const poSnapshot = logData?.poSnapshot || null;

      return {
        isPreviewMode: true,
        templateSelected: true,
        purchaseOrderId: poSnapshot?._id,
        purchaseOrderNumber: poSnapshot?.purchaseOrderNumber || '',
        purchaseOrderDate: poSnapshot?.purchaseOrderDate || null,
        templateName: poSnapshot?.templateName || '',
        status: poSnapshot?.status || null,

        selectedSupplier: poSnapshot?.supplierId
          ? {
              _id: poSnapshot?.supplierId,
              label: poSnapshot?.supplierFactoryDetails?.supplierName || 'Unknown Supplier',
              value: poSnapshot?.supplierId,
            }
          : null,
        selectedFactory: poSnapshot?.factoryId
          ? {
              _id: poSnapshot?.factoryId,
              label: poSnapshot?.supplierFactoryDetails?.factoryName || 'Unknown Factory',
              value: poSnapshot?.factoryId,
            }
          : null,
        selectedTemplate: null,

        companyLogo: poSnapshot?.companyLogo || creationState.companyLogo,

        companyDetails: poSnapshot?.companyDetails || {},
        userDetails: poSnapshot?.userDetails || {},
        supplierDetails: poSnapshot?.supplierFactoryDetails || {},

        shippingDetails: {
          shipmentMethod: poSnapshot?.shippingDetails?.shippingMethod || '',
          incoterms: poSnapshot?.shippingDetails?.incoterms || '',
          shippingCosts: poSnapshot?.shippingDetails?.shippingAndHandlingCosts || '',
          portOfLanding: poSnapshot?.shippingDetails?.portOfLanding || '',
          portOfLoading: poSnapshot?.shippingDetails?.portOfLading || '',
        },

        grandTotal: {
          grandTotalUnits: poSnapshot?.grandTotal?.grandTotalUnits || '',
          grandTotalPriceAfterDiscount: poSnapshot?.grandTotal?.grandTotalPriceAfterDiscount || '',
          shippingCosts: poSnapshot?.shippingDetails?.shippingAndHandlingCosts || '',
        },

        orderDetails: (poSnapshot?.orderDetails || []).map((order) => ({
          id: order.id || `snapshot-${Date.now()}-${Math.random()}`,
          parentRowId: order.parentRowId || null,
          isSubRow: order.isSubRow || false,

          packType: order.packType || '',
          productNo: order.productNumber || order.productNo || '',
          colourWay: order.colourway || order.colourWay || '',
          skuNumber: order.skuNumber || '',
          size: order.size || '',
          length: order.length || '',
          ratio: order.ratio || 0,
          packs: order.packs || 0,
          quantity: order.quantity || 0,
          location: order.location || '',
          unitPrice: order.unitPrice || 0,
          discount: order.discountPercent || 0,
          discountPercent: order.discountPercent || 0,
          priceAfterDiscount: order.priceAfterDiscount || 0,
          total: order.total || 0,
          approvedByDate: order.approvedByDate || null,
          exFactoryDate: order.exFactoryDate || null,
          shipmentDate: order.shipmentDate || null,
          colourwayName: order.colourwayName || order.colourWayName || '',
          sizeName: order.sizeName || '',
          lengthName: order.lengthName || '',
          locationName: order.locationName || '',
          productObjectId: order.productNumber || order.productNo || '',
          colourwayObjectId: order.colourway || order.colourWay || '',
          sizeObjectId: order.size || '',
          lengthObjectId: order.length || '',
        })),

        sections: (poSnapshot.sections || [])
          .filter((section) => {
            const standardSectionKeys = ['po-details', 'company-supplier', 'order-details', 'shipping-details', 'grand-total'];
            return section.sectionKey && !standardSectionKeys.includes(section.sectionKey);
          })
          .map((section) => processSections([section])[0])
          .filter(Boolean),

        POCreatedDate: poSnapshot.createdAt ? new Date(poSnapshot.createdAt) : new Date(),
        POSubmittedDate: poSnapshot.submittedDate ? new Date(poSnapshot.submittedDate) : null,
        PORevisedDate: poSnapshot.revisedDate ? new Date(poSnapshot.revisedDate) : null,

        isLoading: false,
      };
    },
    [creationState.companyLogo, processSections],
  );

  const effectivePoProductDetails = useMemo(() => {
    if (creationState.isPreviewMode) {
      const mockSupplier = creationState.selectedSupplier
        ? {
            _id: creationState.selectedSupplier._id,
            firstName: creationState.supplierDetails?.supplierName?.split(' ')[0] || '',
            lastName: creationState.supplierDetails?.supplierName?.split(' ').slice(1).join(' ') || '',
            email: creationState.supplierDetails?.supplierContactEmail || '',
            streetAddress: creationState.supplierDetails?.supplierAddress || '',
            address: creationState.supplierDetails?.supplierAddress || '',
            code: creationState.supplierDetails?.supplierCode || '',
            internalPointContact: creationState.supplierDetails?.supplierContactName || '',
            companies: [
              {
                companyName: companyURL,
                supplierName: creationState.supplierDetails?.supplierName || '',
                code: creationState.supplierDetails?.supplierCode || '',
                streetAddress: creationState.supplierDetails?.supplierAddress || '',
                internalPointContact: {
                  firstName: creationState.supplierDetails?.supplierContactName?.split(' ')[0] || '',
                  lastName: creationState.supplierDetails?.supplierContactName?.split(' ').slice(1).join(' ') || '',
                  email: creationState.supplierDetails?.supplierContactEmail || '',
                },
              },
            ],
          }
        : null;

      const mockFactory = creationState.selectedFactory
        ? {
            _id: creationState.selectedFactory._id,
            factoryName: creationState.supplierDetails?.factoryName || '',
            factoryCode: creationState.supplierDetails?.factoryCode || '',
            name: creationState.supplierDetails?.factoryName || '',
            code: creationState.supplierDetails?.factoryCode || '',
            paymentTerms: creationState.supplierDetails?.paymentTerms || '',
            streetAddress: creationState.supplierDetails?.factoryAddress || '',
            address: creationState.supplierDetails?.factoryAddress || '',
            buildingNo: '',
            townCity: '',
            zipCode: '',
            contactPersonEmail: creationState.supplierDetails?.factoryContactEmail || '',
            email: creationState.supplierDetails?.factoryContactEmail || '',
            internalPointContact: {
              firstName: creationState.supplierDetails?.factoryContactName?.split(' ')[0] || '',
              lastName: creationState.supplierDetails?.factoryContactName?.split(' ').slice(1).join(' ') || '',
              email: creationState.supplierDetails?.factoryContactEmail || '',
            },
          }
        : null;

      const mockDevelopmentOrders = [];
      const seenProductIds = new Set();

      (creationState.orderDetails || []).forEach((order) => {
        const productId = order.productObjectId || order.productNo;
        const productNumber = order.productNo;

        if (productId && productNumber && !seenProductIds.has(productId)) {
          seenProductIds.add(productId);
          mockDevelopmentOrders.push({
            _id: productId,
            number: productNumber,
            colourwayLibraries: [],
          });
        }
      });

      return {
        companyDetails: {
          ...creationState.companyDetails,
          companyName: creationState.companyDetails?.companyName || '',
          companyRegistration: creationState.companyDetails?.companyNumber || creationState.companyDetails?.companyRegistration || '',
          vatRegistrationNumber: creationState.companyDetails?.vatRegistrationNo || creationState.companyDetails?.vatRegistrationNumber || '',
          streetAddress: creationState.companyDetails?.billingAddress || creationState.companyDetails?.streetAddress || '',
          buildingNumber: creationState.companyDetails?.buildingNumber || '',
          city: creationState.companyDetails?.city || '',
          zipCode: creationState.companyDetails?.zipCode || '',
          distributionCentres: creationState.companyDetails?.distributionCentres || [],
          contactPerson: creationState.companyDetails?.contactPerson || '',
          contactPersonEmail: creationState.companyDetails?.contactPersonEmail || '',
          contactPersonId: creationState.companyDetails?.contactPersonId || '',
        },
        suppliers: mockSupplier ? [mockSupplier] : [],
        supplierFactories: mockFactory ? [mockFactory] : [],
        developmentOrders: mockDevelopmentOrders,
      };
    }
    return poProductDetails;
  }, [
    creationState.isPreviewMode,
    creationState.companyDetails,
    creationState.selectedSupplier,
    creationState.selectedFactory,
    creationState.supplierDetails,
    companyURL,
    poProductDetails,
  ]);

  const getSectionBySectionKey = useCallback(
    (sectionKey) => {
      return creationState.sections?.find((section) => section.sectionKey === sectionKey);
    },
    [creationState.sections],
  );

  const fetchTemplateList = useCallback(async () => {
    try {
      setCreationState((prev) => ({ ...prev, isLoading: true }));
      await reduxDispatch(poTemplateAction.fetchPurchaseOrderTemplateList({ isActive: true }));
      setCreationState((prev) => ({ ...prev, isLoading: false }));
    } catch (error) {
      setCreationState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [reduxDispatch]);

  const fetchProductDetails = useCallback(
    async (devOrderId) => {
      if (!devOrderId) return;
      try {
        setCreationState((prev) => ({ ...prev, isLoading: true }));
        await reduxDispatch(productPurchaseOrderAction.fetchProductDetailsForPO(devOrderId));
        setCreationState((prev) => ({ ...prev, isLoading: false }));
      } catch (error) {
        setCreationState((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [reduxDispatch],
  );

  const fetchTemplateDetails = useCallback(
    async (templateId) => {
      if (!templateId) return;
      try {
        setCreationState((prev) => ({ ...prev, isLoading: true }));
        await reduxDispatch(productPurchaseOrderAction.fetchPOTemplateDetailsForProduct(templateId));
        setCreationState((prev) => ({ ...prev, isLoading: false }));
      } catch (error) {
        setCreationState((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [reduxDispatch],
  );

  const fetchPurchaseOrderDetails = useCallback(
    async (orderId) => {
      if (!orderId) return;
      try {
        setCreationState((prev) => ({ ...prev, isLoading: true }));
        await reduxDispatch(productPurchaseOrderAction.fetchPurchaseOrderDetails(orderId));
        setCreationState((prev) => ({ ...prev, isLoading: false }));
      } catch (error) {
        setCreationState((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [reduxDispatch],
  );

  const clearTemplateDetails = useCallback(() => {
    reduxDispatch(productPurchaseOrderAction.clearPOTemplateDetailsForProduct());

    setCreationState((prev) => ({
      ...prev,
      sections: [],
      sectionValues: {},
      templateSelected: false,
      companyLogo,
      shippingDetails: {
        shipmentMethod: '',
        incoterms: '',
        shippingCosts: '',
        portOfLanding: '',
        portOfLoading: '',
      },
      grandTotal: {
        grandTotalUnits: '',
        grandTotalPriceAfterDiscount: '',
        shippingCosts: '',
      },
      orderDetails: [],
    }));
  }, [reduxDispatch, companyLogo]);

  const refreshPurchaseOrder = useCallback(async () => {
    if (creationState.purchaseOrderId) {
      try {
        setCreationState((prev) => ({ ...prev, isLoading: true }));
        await reduxDispatch(productPurchaseOrderAction.fetchPurchaseOrderDetails(creationState.purchaseOrderId));
      } catch (error) {
        setCreationState((prev) => ({ ...prev, isLoading: false }));
      }
    }
  }, [creationState.purchaseOrderId, reduxDispatch]);

  const handleRevisionTracking = useCallback((currentOrderDetails, originalOrderDetails) => {
    if (!originalOrderDetails || originalOrderDetails.length === 0) {
      return currentOrderDetails;
    }

    return currentOrderDetails.map((currentOrder, index) => {
      const originalOrder = originalOrderDetails[index];
      if (!originalOrder) return currentOrder;

      const revisedOrder = { ...currentOrder };

      const dateFields = [
        { current: 'exFactoryDate', revised: 'revisedExFactoryDate' },
        { current: 'approvedByDate', revised: 'revisedApprovedByDate' },
        { current: 'shipmentDate', revised: 'revisedShipmentDate' },
      ];

      dateFields.forEach(({ current, revised }) => {
        const currentValue = currentOrder[current];
        const originalValue = originalOrder[current];

        if (currentValue && originalValue && new Date(currentValue).getTime() !== new Date(originalValue).getTime()) {
          if (!revisedOrder[revised]) {
            revisedOrder[revised] = originalValue;
          }
        } else if (originalOrder[revised]) {
          revisedOrder[revised] = originalOrder[revised];
        }
      });

      const actualFields = [
        { current: 'actualTotalUnits', original: 'quantity', revised: 'revisedTotalUnits' },
        { current: 'actualExFactoryDate', original: 'exFactoryDate', revised: 'revisedExFactoryDate' },
        { current: 'actualApprovedByDate', original: 'approvedByDate', revised: 'revisedApprovedByDate' },
        { current: 'actualShipmentDate', original: 'shipmentDate', revised: 'revisedShipmentDate' },
      ];

      actualFields.forEach(({ current, original, revised }) => {
        const currentValue = currentOrder[current];
        const originalValue = originalOrder[original];

        if (currentValue != null && originalValue != null && !revisedOrder[revised]) {
          let shouldCreateRevision = false;

          if (current.includes('Date')) {
            const currentDate = new Date(currentValue).getTime();
            const originalDate = new Date(originalValue).getTime();
            shouldCreateRevision = currentDate !== originalDate;
          } else {
            const currentNum = parseFloat(currentValue || 0);
            const originalNum = parseFloat(originalValue || 0);
            shouldCreateRevision = currentNum !== originalNum;
          }

          if (shouldCreateRevision) {
            revisedOrder[revised] = originalValue;
          }
        } else if (originalOrder[revised]) {
          revisedOrder[revised] = originalOrder[revised];
        }
      });

      return revisedOrder;
    });
  }, []);

  const getOriginalPurchaseOrderData = useCallback(
    async (purchaseOrderId) => {
      try {
        if (purchaseOrderDetails && purchaseOrderDetails._id === purchaseOrderId) {
          return purchaseOrderDetails;
        }

        const response = await reduxDispatch(productPurchaseOrderAction.fetchPurchaseOrderDetails(purchaseOrderId));
        return response?.data || purchaseOrderDetails;
      } catch (error) {
        return purchaseOrderDetails;
      }
    },
    [reduxDispatch, purchaseOrderDetails],
  );

  const transformToBackendPayload = useCallback(
    async (originalPurchaseOrderData = null) => {
      const templateName = creationState.selectedTemplate?.label || creationState.templateName || '';
      if (!templateName.trim()) {
        throw new Error('Template name is required');
      }

      const validOrderDetails = (creationState.orderDetails || []).filter((order) => order && Object.keys(order).length > 1 && order.packType);

      if (validOrderDetails.length === 0) {
        throw new Error('At least one order detail with pack type is required');
      }

      return {
        supplierId: creationState?.selectedSupplier?._id || null,
        factoryId: creationState?.selectedFactory?._id || null,
        poTemplateId: creationState?.selectedTemplate?.value || null,
        templateName: creationState?.selectedTemplate?.label || creationState?.templateName || '',
        purchaseOrderNumber: creationState.purchaseOrderNumber?.trim() || '',
        purchaseOrderDate: creationState?.purchaseOrderDate || null,
        companyLogo: creationState?.companyLogo || '',
        originalPOImage: creationState?.originalPOImage || '',

        companyDetails: {
          companyName: poProductDetails?.companyDetails?.companyName || '',
          companyNumber: poProductDetails?.companyDetails?.companyRegistration || '',
          vatRegistrationNumber: poProductDetails?.companyDetails?.vatRegistrationNumber || '',
          paymentTerms:
            creationState?.selectedFactory?.paymentTerms ||
            poProductDetails?.supplierFactories?.find((f) => f._id === creationState.selectedFactory?._id)?.paymentTerms ||
            '',
          billingAddress: `${poProductDetails?.companyDetails?.streetAddress || ''} ${poProductDetails?.companyDetails?.buildingNumber || ''} ${
            poProductDetails?.companyDetails?.city || ''
          } ${poProductDetails?.companyDetails?.zipCode || ''}`.trim(),
          contactPersonId: creationState.companyDetails?.contactPersonId?.trim() || null,
          contactPersonName: creationState.companyDetails?.contactPerson || '',
          contactPersonEmail: creationState.companyDetails?.contactPersonEmail || '',
          createdByName: userInfo ? `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() : '',
          createdByEmail: userInfo?.email || '',
          shippingAddress: poProductDetails?.companyDetails?.distributionCentres?.[0]
            ? `${poProductDetails.companyDetails.distributionCentres[0].distributionName || ''} ${
                poProductDetails.companyDetails.distributionCentres[0].buildingNumber || ''
              } ${poProductDetails.companyDetails.distributionCentres[0].streetAddress || ''} ${
                poProductDetails.companyDetails.distributionCentres[0].city || ''
              } ${poProductDetails.companyDetails.distributionCentres[0].postalCode || ''} ${
                poProductDetails.companyDetails.distributionCentres[0].countryName || ''
              }`.trim()
            : '',
        },

        supplierFactoryDetails: {
          supplierName: (() => {
            const supplier = poProductDetails?.suppliers?.find((s) => s._id === creationState.selectedSupplier?._id);
            const company = supplier?.companies?.find((c) => c.companyName);
            return company?.supplierName || `${supplier?.firstName || ''} ${supplier?.lastName || ''}`.trim() || '';
          })(),
          supplierCode: (() => {
            const supplier = poProductDetails?.suppliers?.find((s) => s._id === creationState.selectedSupplier?._id);
            const company = supplier?.companies?.find((c) => c.companyName);
            return company?.code || supplier?.code || '';
          })(),
          supplierContactName: (() => {
            const supplier = poProductDetails?.suppliers?.find((s) => s._id === creationState.selectedSupplier?._id);
            const company = supplier?.companies?.find((c) => c.companyName);
            const contact = company?.internalPointContact;
            return contact ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() : supplier?.internalPointContact || '';
          })(),
          supplierContactEmail: (() => {
            const supplier = poProductDetails?.suppliers?.find((s) => s._id === creationState.selectedSupplier?._id);
            const company = supplier?.companies?.find((c) => c.companyName);
            return company?.internalPointContact?.email || supplier?.email || '';
          })(),
          supplierAddress: (() => {
            const supplier = poProductDetails?.suppliers?.find((s) => s._id === creationState.selectedSupplier?._id);
            const company = supplier?.companies?.find((c) => c.companyName);
            return company?.streetAddress || supplier?.streetAddress || supplier?.address || '';
          })(),
          factoryName: (() => {
            const factory = poProductDetails?.supplierFactories?.find((f) => f._id === creationState.selectedFactory?._id);
            return factory?.factoryName || factory?.name || '';
          })(),
          factoryCode: (() => {
            const factory = poProductDetails?.supplierFactories?.find((f) => f._id === creationState.selectedFactory?._id);
            return factory?.factoryCode || factory?.code || '';
          })(),
          factoryContactName: (() => {
            const factory = poProductDetails?.supplierFactories?.find((f) => f._id === creationState.selectedFactory?._id);
            const contact = factory?.internalPointContact;
            return contact ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() : '';
          })(),
          factoryContactEmail: (() => {
            const factory = poProductDetails?.supplierFactories?.find((f) => f._id === creationState.selectedFactory?._id);
            return factory?.internalPointContact?.email || factory?.contactPersonEmail || factory?.email || '';
          })(),
          factoryAddress: (() => {
            const factory = poProductDetails?.supplierFactories?.find((f) => f._id === creationState.selectedFactory?._id);
            return (
              `${factory?.streetAddress || ''} ${factory?.buildingNo || ''} ${factory?.townCity || ''} ${factory?.zipCode || ''} ${
                factory?.country || ''
              }`.trim() ||
              factory?.address ||
              ''
            );
          })(),
        },

        shippingDetails: {
          shippingMethod: creationState.shippingDetails?.shipmentMethod?.toString()?.trim() || '',
          incoterms: creationState.shippingDetails?.incoterms?.toString()?.trim() || null,
          shippingAndHandlingCosts: Number(creationState.shippingDetails?.shippingCosts) || 0,
          portOfLanding: creationState.shippingDetails?.portOfLanding?.toString()?.trim() || '',
          portOfLading: creationState.shippingDetails?.portOfLoading?.toString()?.trim() || '',
        },

        orderDetails: (() => {
          const currentOrderDetails = (creationState.orderDetails || [])
            .filter((order) => order && Object.keys(order).length > 1 && order.packType)
            .map((order) => ({
              id: order.id,
              parentRowKey: order.parentRowKey || null,
              parentRowId: order.parentRowId || null,
              isSubRow: order.isSubRow || false,
              packType: order.packType?.value || order.packType || '',
              productNumber: order.productNo?.value || order.productNo || null,
              colourway: order.colourWay?.value || order.colourWay || null,
              skuNumber: order.skuNumber || '',
              size: order.size?.value || order.size || null,
              length: order.length?.value || order.length || null,
              ratio: Number(order.ratio) || 0,
              packs: Number(order.packs) || 0,
              quantity: Number(order.quantity) || 0,
              location: order.location?.toString()?.trim() || '',
              unitPrice: Number(order.unitPrice) || 0,
              discountPercent: Number(order.discount) || Number(order.discountPercent) || 0,
              priceAfterDiscount: Number(order.priceAfterDiscount) || 0,
              total: Number(order.total) || 0,
              approvedByDate: toLocalDateOnly(order.approvedByDate) ?? order.approvedByDate ?? null,
              exFactoryDate: toLocalDateOnly(order.exFactoryDate) ?? order.exFactoryDate ?? null,
              shipmentDate: toLocalDateOnly(order.shipmentDate) ?? order.shipmentDate ?? null,
              actualApprovedByDate: toLocalDateOnly(order.actualApprovedByDate) ?? order.actualApprovedByDate ?? null,
              actualExFactoryDate: toLocalDateOnly(order.actualExFactoryDate) ?? order.actualExFactoryDate ?? null,
              actualShipmentDate: toLocalDateOnly(order.actualShipmentDate) ?? order.actualShipmentDate ?? null,
              actualTotalUnits: Number(order.actualTotalUnits) || null,
              revisedApprovedByDate: toLocalDateOnly(order.revisedApprovedByDate) ?? order.revisedApprovedByDate ?? null,
              revisedExFactoryDate: toLocalDateOnly(order.revisedExFactoryDate) ?? order.revisedExFactoryDate ?? null,
              revisedShipmentDate: toLocalDateOnly(order.revisedShipmentDate) ?? order.revisedShipmentDate ?? null,
              revisedTotalUnits: Number(order.revisedTotalUnits) || null,
            }));
          currentOrderDetails.forEach((parent) => {
            const { packType, parentRowKey, isSubRow } = parent;
            if ((packType ?? '') !== PACK_TYPE_MULTI_SIZE || isSubRow || !parentRowKey) return;
            currentOrderDetails.forEach((row, subIdx) => {
              if (!row.isSubRow || row.parentRowId !== parentRowKey) return;
              DATE_FIELDS_FOR_MULTI_SIZE.forEach((field) => {
                currentOrderDetails[subIdx][field] = parent[field];
              });
            });
          });

          if (originalPurchaseOrderData?.orderDetails) {
            return handleRevisionTracking(currentOrderDetails, originalPurchaseOrderData.orderDetails);
          }

          return currentOrderDetails;
        })(),

        grandTotal: {
          grandTotalUnits: Number(creationState.grandTotal?.grandTotalUnits) || 0,
          grandTotalPriceAfterDiscount: Number(creationState.grandTotal?.grandTotalPriceAfterDiscount) || 0,
        },
        sections: (creationState.sections || []).map((section) => ({
          ...section,
          columns:
            section.columns?.map(
              (column, colIdx) =>
                column?.map((control, idx) => {
                  const uniqueControlId = `${section.id}_${colIdx}_${control.id}_${idx}`;
                  const hasUserValue = creationState.sectionValues?.hasOwnProperty(uniqueControlId);
                  return {
                    ...control,
                    value: hasUserValue
                      ? creationState.sectionValues[uniqueControlId]
                      : control.value !== undefined && control.value !== null
                      ? control.value
                      : control.defaultValue !== undefined && control.defaultValue !== null
                      ? control.defaultValue
                      : '',
                  };
                }) || [],
            ) || [],
        })),
        createdBy: userInfo?._id?.toString() || userInfo?.userId?.toString() || null,
        modifiedBy: userInfo?._id?.toString() || userInfo?.userId?.toString() || null,
      };
    },
    [creationState, poProductDetails, userInfo, handleRevisionTracking],
  );

  const handlePostCreateActions = useCallback(async (purchaseOrderId) => {
    setCreationState((prev) => ({
      ...prev,
      purchaseOrderId,
      isNavigatingAfterCreate: true,
    }));
  }, []);

  const handlePostUpdateActions = useCallback(
    async (purchaseOrderId) => {
      try {
        if (purchaseOrderId) {
          setCreationState((prev) => ({ ...prev, isRefreshingAfterSave: true }));

          const updatedPOResponse = await reduxDispatch(productPurchaseOrderAction.fetchPurchaseOrderDetails(purchaseOrderId));

          if (updatedPOResponse?.code === 200) {
            setCreationState((prev) => ({
              ...prev,
              purchaseOrderNumber: updatedPOResponse.data.purchaseOrderNumber || prev.purchaseOrderNumber,
              purchaseOrderDate: updatedPOResponse.data.purchaseOrderDate || prev.purchaseOrderDate,
            }));
          }
        }
      } catch (error) {
        console.warn('Post-update actions failed:', error);
      } finally {
        setTimeout(() => {
          setCreationState((prev) => ({ ...prev, isRefreshingAfterSave: false }));
        }, 100);
      }
    },
    [reduxDispatch, setCreationState],
  );

  const savePurchaseOrder = useCallback(async () => {
    try {
      setCreationState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));

      clearAllValidationErrors();

      const validationErrors = {};

      if (!creationState.selectedSupplier?._id) {
        validationErrors.selectedSupplier = t('CREATE_EDIT_PO.VALIDATION.SUPPLIER_REQUIRED') || 'Supplier is required';
      }
      if (!creationState.selectedTemplate?.value) {
        validationErrors.selectedTemplate = t('CREATE_EDIT_PO.VALIDATION.TEMPLATE_REQUIRED') || 'Purchase Order template is required';
      }

      if (!creationState.purchaseOrderNumber?.trim()) {
        validationErrors.purchaseOrderNumber = t('CREATE_EDIT_PO.VALIDATION.PO_NUMBER_REQUIRED') || 'Purchase Order Number is required';
      }

      const orderDetailsArray = creationState.orderDetails || [];

      if (orderDetailsArray.length === 0) {
        validationErrors.orderDetails = t('CREATE_EDIT_PO.VALIDATION.ORDER_DETAILS_REQUIRED') || 'At least one order detail is required';
      } else {
        const parentRows = orderDetailsArray.filter((order) => order && Object.keys(order).length > 1 && !order.isSubRow);

        if (parentRows.length === 0) {
          validationErrors.orderDetails = t('CREATE_EDIT_PO.VALIDATION.ORDER_DETAILS_REQUIRED') || 'At least one order detail is required';
        } else {
          const hasInvalidParentRows = parentRows.some((order) => !order.productNo || !order.packType);

          if (hasInvalidParentRows) {
            validationErrors.orderDetailsProducts = true;
          }
        }
      }

      if (creationState.sections && Array.isArray(creationState.sections)) {
        creationState.sections.forEach((section) => {
          if (section.columns && Array.isArray(section.columns)) {
            section.columns.forEach((column, colIdx) => {
              if (Array.isArray(column)) {
                column.forEach((control, idx) => {
                  if (control.required === true) {
                    const uniqueControlId = `${section.id}_${colIdx}_${control.id}_${idx}`;
                    const hasUserValue = creationState.sectionValues?.hasOwnProperty(uniqueControlId);
                    const controlValue = hasUserValue ? creationState.sectionValues[uniqueControlId] : control.value || control.defaultValue || '';

                    let isEmpty = false;

                    if (control.type === 'Dropdown' || control.type === 'DatePicker') {
                      isEmpty = controlValue === null || controlValue === undefined || controlValue === '';
                    } else if (control.type === 'Checkbox') {
                      isEmpty = !Array.isArray(controlValue) || controlValue.length === 0;
                    } else if (control.type === 'RadioButton') {
                      isEmpty = !controlValue || controlValue === '';
                    } else {
                      isEmpty = !controlValue || (typeof controlValue === 'string' && controlValue.trim() === '');
                    }

                    if (isEmpty) {
                      const errorMessage =
                        t('CREATE_EDIT_PO.VALIDATION.FIELD_REQUIRED', { field: control.label || control.id }) || `${control.label || control.id} is required`;
                      validationErrors[uniqueControlId] = errorMessage;
                    }
                  }
                });
              }
            });
          }
        });
      }

      if (Object.keys(validationErrors).length > 0) {
        setCreationState((prev) => ({
          ...prev,
          isLoading: false,
          validationErrors,
        }));

        const errorMessage = t('CREATE_EDIT_PO.VALIDATION.PLEASE_FILL_REQUIRED_FIELDS') || 'Please fill in all required fields';
        toasterMsg(errorMessage, 'danger');

        return { success: false, error: errorMessage, validationErrors };
      }

      let originalPurchaseOrderData = null;
      if (creationState.purchaseOrderId) {
        originalPurchaseOrderData = await getOriginalPurchaseOrderData(creationState.purchaseOrderId);
      }

      const backendPayload = await transformToBackendPayload(originalPurchaseOrderData);
      backendPayload.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? undefined;

      const requiredFields = {
        supplierId: backendPayload.supplierId,
        poTemplateId: backendPayload.poTemplateId,
        templateName: backendPayload.templateName,
      };

      const missingFields = Object.entries(requiredFields)
        .filter(([, value]) => !value)
        .map(([key]) => key);

      if (missingFields.length > 0) {
        const errorMessage = `Backend validation failed - Missing required fields: ${missingFields.join(', ')}`;
        throw new Error(errorMessage);
      }

      let response;
      if (creationState.purchaseOrderId) {
        response = await reduxDispatch(productPurchaseOrderAction.updateProductPurchaseOrder(creationState.purchaseOrderId, backendPayload, t));
      } else {
        response = await reduxDispatch(productPurchaseOrderAction.createProductPurchaseOrder(backendPayload, t));
      }
      if (response?.code === 200) {
        const isUpdate = !!creationState.purchaseOrderId;
        const purchaseOrderId = response.data._id || response.data.id;

        setCreationState((prev) => ({
          ...prev,
          purchaseOrderId,
          isLoading: false,
          error: null,
          validationErrors: {},
        }));

        if (!isUpdate) {
          await handlePostCreateActions(purchaseOrderId);
        } else {
          await handlePostUpdateActions(purchaseOrderId);
        }

        return { success: true, data: response.data, isUpdate, purchaseOrderId };
      }

      const errorMessage = response?.data?.errMsg || response?.message || 'Failed to save purchase order';
      throw new Error(errorMessage);
    } catch (error) {
      console.error('Save Purchase Order Error:', error);

      setCreationState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));

      toasterMsg(error.message, 'danger');

      return { success: false, error: error.message };
    }
  }, [
    transformToBackendPayload,
    creationState.purchaseOrderId,
    creationState.selectedSupplier,
    creationState.selectedFactory,
    creationState.selectedTemplate,
    creationState.purchaseOrderNumber,
    creationState.orderDetails,
    reduxDispatch,
    t,
    clearAllValidationErrors,
    handlePostCreateActions,
    handlePostUpdateActions,
    getOriginalPurchaseOrderData,
  ]);

  const updateSectionsOrder = useCallback(
    async (rearrangedSections) => {
      setCreationState((prev) => ({
        ...prev,
        sections: rearrangedSections,
      }));

      if (creationState.purchaseOrderId) {
        try {
          await reduxDispatch(productPurchaseOrderAction.rearrangePurchaseOrderSections(creationState.purchaseOrderId, rearrangedSections, t));
        } catch (error) {
          setCreationState((prev) => ({
            ...prev,
            sections: prev.sections,
          }));
        }
      }
    },
    [creationState.purchaseOrderId, reduxDispatch, t],
  );

  const initializePreviewMode = useCallback(
    (logData) => {
      const populatedData = populateFromLogHistoryData(logData);
      if (populatedData) {
        setCreationState((prev) => ({
          ...prev,
          ...populatedData,
        }));
      }
    },
    [populateFromLogHistoryData],
  );

  const contextValue = useMemo(
    () => ({
      creationState,
      setCreationState,
      getBrandIdForFilter,
      handleSectionValueChange,
      handlePOFieldChange,
      setValidationError,
      clearValidationError,
      clearAllValidationErrors,
      resetCreationContext,
      clearAllPOData,
      getSectionBySectionKey,
      updateSectionsOrder,
      transformToBackendPayload,
      companyLogo,
      companyURL,
      templateDetails,
      poProductDetails: effectivePoProductDetails,
      isFieldsDisabledByStatus,
      fetchTemplateList,
      fetchProductDetails,
      fetchTemplateDetails,
      fetchPurchaseOrderDetails,
      clearTemplateDetails,
      refreshPurchaseOrder,
      savePurchaseOrder,
      handlePostCreateActions,
      handlePostUpdateActions,
      handleRevisionTracking,
      getOriginalPurchaseOrderData,
      populateFromLogHistoryData,
      initializePreviewMode,
      userInfo,
      currencySymbol,
    }),
    [
      creationState,
      handleSectionValueChange,
      handlePOFieldChange,
      setValidationError,
      clearValidationError,
      clearAllValidationErrors,
      resetCreationContext,
      clearAllPOData,
      getSectionBySectionKey,
      updateSectionsOrder,
      transformToBackendPayload,
      companyLogo,
      companyURL,
      templateDetails,
      effectivePoProductDetails,
      isFieldsDisabledByStatus,
      fetchTemplateList,
      fetchProductDetails,
      fetchTemplateDetails,
      fetchPurchaseOrderDetails,
      clearTemplateDetails,
      refreshPurchaseOrder,
      savePurchaseOrder,
      handlePostCreateActions,
      handlePostUpdateActions,
      handleRevisionTracking,
      getOriginalPurchaseOrderData,
      populateFromLogHistoryData,
      initializePreviewMode,
      userInfo,
      currencySymbol,
    ],
  );

  return <POCreationContext.Provider value={contextValue}>{children}</POCreationContext.Provider>;
}

POCreationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const usePOCreation = () => {
  const context = useContext(POCreationContext);
  if (!context) {
    throw new Error('usePOCreation must be used within a POCreationProvider');
  }
  return context;
};
